import { serializeRotaryKilnsForSave } from './environmental/rotaryKilnPayload.js';
import type { EnvironmentalDataFormData, RotaryKiln } from '../../types/reports.js';

/**
 * Die EINE Nutzlastform eines Formularstands -- fuer das Speichern (PATCH) UND fuer die
 * Vorpruefung (POST /api/reports/validate).
 *
 * Warum gemeinsam: Bis hierher baute nur der Speicherpfad seine Nutzlast um
 * (`serializeRotaryKilnsForSave`), die Vorpruefung schickte `form.getValues()` roh. Damit prueften
 * die beiden Wege NICHT dasselbe -- im Umweltformular mit sichtbarer Folge: Der Speicherpfad
 * schickt `plantKiln` als Referenz-IRI, der rohe Formularstand dagegen das eingebettete Objekt
 * (`{ id, name, allowsOwnMeasurements }`). Aus dem Objekt denormalisiert die Vorpruefung einen
 * Drehofen OHNE Werksbezug (`PlantKiln::$id` traegt keine `report:write`-Gruppe), woraufhin
 * `RotaryKilnsBelongToPlant` je Ofenzeile „Dieser Drehofen gehoert nicht zu diesem Werk" meldete
 * -- an einem Feldpfad, zu dem es kein sichtbares Feld gibt. Das Einreichen blieb dadurch fuer
 * jedes Werk MIT Drehofen gesperrt, obwohl der Einreichen-Endpunkt selbst die Meldung annimmt
 * (Abnahmetest 21.09.2026, Punkt I-3).
 *
 * Deshalb geht ab jetzt jeder Weg, der einen Formularstand zum Server traegt, durch diese
 * Funktion. Ein kuenftiger dritter Weg erbt die Umwandlung damit automatisch; wer sie umgeht,
 * prueft wieder etwas anderes als das, was gespeichert wird.
 */

/**
 * `kind` des Umweltformulars, wie ihn `REPORT_TYPES` traegt (`reportTypes.ts`).
 *
 * Bewusst als eigene Konstante statt eines Imports aus `reportTypes.ts`: jene Datei zieht alle
 * fuenf Formularkomponenten nach sich, und `useReportForm` -- der Hook, den genau diese
 * Komponenten benutzen -- ruft die Funktion hier auf. Der Import waere ein Zyklus.
 * `reportPayload.test.ts` prueft den Gleichstand beider Stellen, der Kommentar allein traegt ihn
 * nicht.
 */
export const ENVIRONMENTAL_KIND = 'environmental';

/**
 * Formt den Formularstand in die Nutzlast um, die der Server erwartet.
 *
 * Nur das Umweltformular braucht heute eine Umformung (Drehoefen, siehe oben); die vier anderen
 * Typen reicht die Funktion unveraendert durch -- absichtlich ohne Kopie, der Aufrufer uebergibt
 * ohnehin einen frischen `form.getValues()`-Schnappschuss.
 */
export function buildReportPayload(
  kind: string,
  values: Record<string, unknown>
): Record<string, unknown> {
  if (kind !== ENVIRONMENTAL_KIND) {
    return values;
  }

  const envValues = values as EnvironmentalDataFormData;

  return {
    ...envValues,
    rotaryKilns: serializeRotaryKilnsForSave(envValues.rotaryKilns ?? ([] as RotaryKiln[])),
  };
}
